import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { labAPI } from '../../utils/api';

function fmt(ts) {
    if (!ts) return '—';
    return new Date(ts).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function calcTAT(start, end) {
    if (!start) return '—';
    const endTime = end ? new Date(end) : new Date();
    const mins = Math.floor((endTime - new Date(start)) / 60000);
    if (mins < 60) return `${mins}m`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function getStatus(row) {
    if (row.status === 'Completed') return { label: '✅ Done', bg: '#dcfce7', color: '#16a34a' };
    if (row.report_time)           return { label: '📝 Resulted', bg: '#ede9fe', color: '#7c3aed' };
    if (row.received_at)           return { label: '🔬 In Lab', bg: '#dbeafe', color: '#1d4ed8' };
    if (row.collected_at)          return { label: '🧬 Collected', bg: '#fef3c7', color: '#92400e' };
    return                                { label: '⏳ Pending', bg: '#f1f5f9', color: '#64748b' };
}

const PRIORITY_COLOR = {
    STAT:    { bg: '#fef2f2', color: '#dc2626' },
    Urgent:  { bg: '#fff7ed', color: '#ea580c' },
    Routine: { bg: '#f0fdf4', color: '#16a34a' },
};

export default function LabSampleTracking() {
    const [rows, setRows]           = useState([]);
    const [loading, setLoading]     = useState(true);
    const [search, setSearch]       = useState('');
    const pkDate = (offset = 0) => new Date(Date.now() + 5*60*60*1000 + offset).toISOString().split('T')[0];
    const [filterDate, setFilterDate] = useState(pkDate());
    const [marking, setMarking]     = useState(null);

    const today     = pkDate();
    const yesterday = pkDate(-86400000);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await labAPI.getSampleTracking({ date: filterDate, search: search || undefined });
            setRows(res.data);
        } catch { toast.error('Failed to load tracking data'); setRows([]); }
        finally { setLoading(false); }
    }, [filterDate, search]);

    useEffect(() => { load(); }, [load]);

    const markReceived = async (order_id) => {
        setMarking(order_id);
        try {
            await labAPI.markSampleReceived(order_id);
            toast.success('Sample marked as received');
            load();
        } catch { toast.error('Failed to mark received'); }
        finally { setMarking(null); }
    };

    const thStyle = {
        padding: '10px 14px', textAlign: 'left', fontSize: 11,
        fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0',
        background: '#f8fafc', whiteSpace: 'nowrap',
    };
    const tdStyle = { padding: '10px 14px', fontSize: 12, borderBottom: '1px solid #f1f5f9' };

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: '#7c3aed', margin: 0 }}>🧬 Sample Tracking</h2>
                    <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>End-to-end sample journey — from booking to report</p>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Search */}
                    <input
                        placeholder="🔍 Search by Name or MR No"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && load()}
                        style={{ padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, width: 220 }}
                    />
                    {/* Date quick buttons */}
                    {[['Today', today], ['Yesterday', yesterday]].map(([label, val]) => (
                        <button key={label} onClick={() => setFilterDate(val)}
                            style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: 12,
                                fontWeight: filterDate === val ? 700 : 400,
                                background: filterDate === val ? '#7c3aed' : 'white',
                                color: filterDate === val ? 'white' : '#374151' }}>
                            {label}
                        </button>
                    ))}
                    <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)}
                        style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12 }} />
                    <button onClick={load}
                        style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: 13, background: 'white' }}>
                        🔄
                    </button>
                </div>
            </div>

            {/* Summary chips */}
            {!loading && rows.length > 0 && (() => {
                const done      = rows.filter(r => r.status === 'Completed').length;
                const inLab     = rows.filter(r => r.received_at && r.status !== 'Completed').length;
                const collected = rows.filter(r => r.collected_at && !r.received_at).length;
                const pending   = rows.filter(r => !r.collected_at).length;
                return (
                    <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                        {[
                            [`Total: ${rows.length}`, '#f5f3ff', '#7c3aed'],
                            [`✅ Done: ${done}`, '#dcfce7', '#16a34a'],
                            [`🔬 In Lab: ${inLab}`, '#dbeafe', '#1d4ed8'],
                            [`🧬 Collected: ${collected}`, '#fef3c7', '#92400e'],
                            [`⏳ Pending: ${pending}`, '#f1f5f9', '#64748b'],
                        ].map(([label, bg, color]) => (
                            <span key={label} style={{ padding: '5px 14px', borderRadius: 20, background: bg, color, fontSize: 12, fontWeight: 700 }}>{label}</span>
                        ))}
                    </div>
                );
            })()}

            {/* Table */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Loading...</div>
            ) : rows.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, background: 'white', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>🧬</div>
                    <p style={{ color: '#64748b' }}>No samples found for selected date</p>
                </div>
            ) : (
                <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                        <thead>
                            <tr>
                                <th style={thStyle}>Priority</th>
                                <th style={thStyle}>Type</th>
                                <th style={thStyle}>Name</th>
                                <th style={thStyle}>MR No</th>
                                <th style={thStyle}>Booking Time</th>
                                <th style={thStyle}>Sample Collection</th>
                                <th style={thStyle}>Sample Receive</th>
                                <th style={thStyle}>Report Time</th>
                                <th style={thStyle}>TAT</th>
                                <th style={thStyle}>Status</th>
                                <th style={thStyle}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => {
                                const pc  = PRIORITY_COLOR[row.priority] || PRIORITY_COLOR.Routine;
                                const st  = getStatus(row);
                                const isSTAT = row.priority === 'STAT';
                                const needsReceive = row.collected_at && !row.received_at && row.status !== 'Completed';
                                return (
                                    <tr key={row.id} style={{ background: isSTAT ? '#fff5f5' : 'white' }}>
                                        <td style={tdStyle}>
                                            <span style={{ background: pc.bg, color: pc.color, padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>
                                                {isSTAT ? '🚨 ' : ''}{row.priority}
                                            </span>
                                        </td>
                                        <td style={tdStyle}>
                                            {row.patient_type === 'Emergency'
                                                ? <span style={{ background: '#fef2f2', color: '#dc2626', padding: '3px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>🚨 Emergency</span>
                                                : row.patient_type === 'Admitted'
                                                ? <span style={{ background: '#dbeafe', color: '#1d4ed8', padding: '3px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>🛏️ Admitted</span>
                                                : <span style={{ background: '#dcfce7', color: '#16a34a', padding: '3px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>🚶 Walking</span>
                                            }
                                        </td>
                                        <td style={tdStyle}>
                                            <div style={{ fontWeight: 600, fontSize: 13 }}>{row.full_name}</div>
                                            <div style={{ fontSize: 10, color: '#64748b' }}>{row.order_number}</div>
                                            {row.collection_point && <div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 600 }}>📍 {row.collection_point}</div>}
                                        </td>
                                        <td style={{ ...tdStyle, fontWeight: 700, color: '#7c3aed' }}>{row.mrn}</td>
                                        <td style={tdStyle}>
                                            <span style={{ fontWeight: 600 }}>{fmt(row.order_time)}</span>
                                        </td>
                                        <td style={tdStyle}>
                                            {row.collected_at
                                                ? <span style={{ color: '#16a34a', fontWeight: 600 }}>✅ {fmt(row.collected_at)}</span>
                                                : <span style={{ color: '#94a3b8' }}>—</span>}
                                        </td>
                                        <td style={tdStyle}>
                                            {row.received_at
                                                ? <span style={{ color: '#1d4ed8', fontWeight: 600 }}>📥 {fmt(row.received_at)}</span>
                                                : <span style={{ color: '#94a3b8' }}>—</span>}
                                        </td>
                                        <td style={tdStyle}>
                                            {row.report_time
                                                ? <span style={{ color: '#7c3aed', fontWeight: 600 }}>📝 {fmt(row.report_time)}</span>
                                                : <span style={{ color: '#94a3b8' }}>—</span>}
                                        </td>
                                        <td style={tdStyle}>
                                            <span style={{ fontWeight: 700, color: row.status === 'Completed' ? '#16a34a' : '#d97706' }}>
                                                {calcTAT(row.order_time, row.report_time)}
                                            </span>
                                        </td>
                                        <td style={tdStyle}>
                                            <span style={{ background: st.bg, color: st.color, padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>
                                                {st.label}
                                            </span>
                                        </td>
                                        <td style={tdStyle}>
                                            {needsReceive && (
                                                <button
                                                    onClick={() => markReceived(row.id)}
                                                    disabled={marking === row.id}
                                                    style={{ padding: '5px 12px', background: '#1d4ed8', color: 'white', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                    {marking === row.id ? '...' : '📥 Mark Received'}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
